import axios, { AxiosError } from 'axios';
import dayjs from 'dayjs';
import * as authActions from '../actions/auth';
import { IAuthToken } from '../models/auth';
import config from '../services/config';
import NotificationService, {
  NotificationSeverity
} from '../services/NotificationService';
import { store } from '../store';
const ClientOAuth2 = require('client-oauth2');

var oauthClient = new ClientOAuth2(config.auth);

export interface IPaginatedResponse<T> {
  page: number;
  perPage: number;
  total: number;
  totalPages: number;
  data: T[];
  supportingData: any;
}

export interface IApiSuccessResponse<T> {
  success: boolean;
  data?: T;
}

interface IAccessToken {
  accessToken: string;
  refreshToken: string;
  expires: string; // Date string
  tokenType: string;
  data: any; // Response object from API
}

interface IRequestOptions {
  url: string;
  data?: any;
  query?: { [key: string]: any };
  contentType?: string | boolean;
  processData?: boolean;
}

class ApiService {
  url: string;
  headers: any;
  token: IAccessToken;
  refreshing: boolean = false;

  constructor(url) {
    this.get = this.get.bind(this);
    this.post = this.post.bind(this);
    this.put = this.put.bind(this);
    this.delete = this.delete.bind(this);
    this.buildQuery = this.buildQuery.bind(this);
    this.buildUrl = this.buildUrl.bind(this);
    this.request = this.request.bind(this);
    this.url = url;
    this.headers = {};
    this.token = null;
  }

  setToken(token: IAccessToken) {
    this.token = token;
  }

  get<T>(opts: IRequestOptions) {
    return this.request<T>('GET', opts);
  }

  post<T>(opts: IRequestOptions) {
    return this.request<T>('POST', opts);
  }

  put<T>(opts: IRequestOptions) {
    return this.request<T>('PUT', opts);
  }

  delete<T>(opts: IRequestOptions) {
    return this.request<T>('DELETE', opts);
  }

  addParam(key, value, url, query) {
    if (query == null) {
      query = '';
    }
    if (query.length > 0 || url.indexOf('?') > -1) {
      query += '&';
    }
    if (query.length === 0 && url.indexOf('?') === -1) {
      query += '?';
    }
    query += key + '=' + encodeURIComponent(value);
    return query;
  }

  buildQuery(url, queryParams) {
    if (queryParams == null) {
      queryParams = {};
    }
    let query = '';
    for (let k in queryParams) {
      const v = queryParams[k];
      if (v == null || v === '') {
        continue;
      }

      // Convert dayjs dates into the format PHP expects
      if (v && v !== '' && dayjs.isDayjs(v)) {
        query = this.addParam(k, v.format(config.phpDateFormat), url, query);
      } else if (v.startDate != null) {
        query = this.addParam(
          'startDate',
          v.startDate.format(config.phpDateFormat),
          url,
          query
        );
        query = this.addParam(
          'endDate',
          v.endDate.format(config.phpDateFormat),
          url,
          query
        );
      } else if (Array.isArray(v)) {
        if (v.length === 0) {
          continue;
        }
        query = this.addParam(k, v.join(','), url, query);
      } else if (v != null && v !== '') {
        query = this.addParam(k, v, url, query);
      }
    }
    return query;
  }

  buildUrl(resource, queryParams) {
    return this.url + resource + this.buildQuery(resource, queryParams);
  }

  isTokenExpired() {
    let token = oauthClient.createToken(
      this.token.accessToken,
      this.token.refreshToken,
      this.token.tokenType,
      this.token.data
    );

    // Build in a 10min buffer where we will proactively refresh the token if it is close to expiring
    let expiresDate = new Date(this.token.expires);
    expiresDate.setMinutes(expiresDate.getMinutes() - 10);
    token.expiresIn(expiresDate);

    return token.expired();
  }

  refreshToken(): Promise<IAuthToken> {
    return new Promise(async (resolve) => {
      // If we are already refreshing, don't attempt a second refresh (as the refresh_token will be invalid)
      if (this.refreshing) {
        await new Promise((resolve) =>
          setInterval(
            () => (!this.refreshing ? resolve(this.token) : null),
            100
          )
        );
      }

      if (this.token == null) {
        return resolve(null);
      }

      if (!this.isTokenExpired()) {
        return resolve(this.token);
      }

      this.refreshing = true;

      store.dispatch(authActions.refreshToken(this.token)).then((token) => {
        this.refreshing = false;
        this.token = token;
        resolve(token);
      });
    });
  }

  request<T>(method, opts: IRequestOptions): Promise<T> {
    const queryParams = opts.query || {};

    return this.refreshToken().then((token: any) => {
      const state = store.getState();

      const headers = Object.assign({}, this.headers, {
        Authorization: token ? `Bearer ${token.accessToken}` : 'None',
        'Content-Type':
          opts.contentType !== false
            ? opts.contentType || 'application/json'
            : undefined
      });
      if (state.common.facility) {
        headers['X-Facility-ID'] = state.common.facility.id;
      }

      const api = axios.create({ baseURL: this.url });

      return api
        .request({
          url: this.buildUrl(opts.url, queryParams),
          method,
          headers,
          data:
            opts.contentType !== false ? JSON.stringify(opts.data) : opts.data
        })
        .then((response) => {
          return response.data;
        })
        .catch((err: AxiosError) => {
          if (err.response.status == 401) {
            store.dispatch(authActions.logout(true));
          }

          if (err.response.status == 403) {
            NotificationService.show({
              message: 'Permission denied',
              severity: NotificationSeverity.ERROR
            });
          }

          return Promise.reject(err.response.data);
        });
    });
  }
}

export default ApiService;
